<script lang="ts">
    import { _ } from "svelte-i18n"
    import MedialibImage from "../../widgets/MedialibImage.svelte"
    import MoreButton from "../../widgets/MoreButton.svelte"
    import { kirbyFetch } from "../../../api/kirby/api"
    import { lang } from "../../../store"
    import { onDestroy, onMount, tick } from "svelte"
    import { animateOnVisible } from "../../../configs/config"

    let { block }: { block: SkewedImageSliderBlock } = $props()
    const cnt = block.content
    let slides = $state(cnt.slides || [])
    let loading = $state(true)
    const DEFAULT_CONTENT_MIN_HEIGHT = 690
    const EXTRA_CONTENT_PADDING_REM = 2.4
    let contentMinHeight = $state(`${DEFAULT_CONTENT_MIN_HEIGHT}px`)

    async function calculateContentMinHeight() {
        if (typeof window === "undefined") {
            contentMinHeight = `${DEFAULT_CONTENT_MIN_HEIGHT}px`
            return
        }

        await tick()
        if (loading) {
            contentMinHeight = `${DEFAULT_CONTENT_MIN_HEIGHT}px`
            return
        }
        if (!contentSliderEl) {
            contentMinHeight = `${DEFAULT_CONTENT_MIN_HEIGHT}px`
            return
        }

        const contentElements = Array.from(contentSliderEl.querySelectorAll<HTMLElement>(".content"))

        const maxHeight = contentElements.reduce((height, el) => {
            const previousMinHeight = el.style.minHeight
            el.style.minHeight = ""
            const measuredHeight = el.offsetHeight
            el.style.minHeight = previousMinHeight
            return Math.max(height, measuredHeight)
        }, 0)

        if (maxHeight > 0) {
            contentMinHeight = `calc(${maxHeight}px + ${EXTRA_CONTENT_PADDING_REM}rem)`
            return
        }

        contentMinHeight = `${DEFAULT_CONTENT_MIN_HEIGHT}px`
    }

    function handleResize() {
        void calculateContentMinHeight()
    }

    type ShiftedImageSliderPage = {
        uuid: string
        url: string
    }

    async function loadSlides(currentLang: string) {
        const pageReferences = (cnt.slides || []).map((el) => el.page_reference?.[0]).filter(Boolean)
        if (!pageReferences.length) {
            slides = cnt.slides || []
            loading = false
            await calculateContentMinHeight()
            return
        }

        const pages =
            (await kirbyFetch<ShiftedImageSliderPage[]>({
                query: `site.index.filterBy('Uuid', 'in', ['${pageReferences.join("','")}'])`,
                select: {
                    uuid: true,
                    url: true,
                },
            })) || []

        slides = (cnt.slides || []).map((item) => {
            const pageUrl = pages.find((page) => page.uuid === item.page_reference?.[0])?.url
            if (!pageUrl) return item
            return {
                ...item,
                link: `/${currentLang}/` + pageUrl.split(`/${currentLang}/`).pop(),
            }
        })
        loading = false
        await calculateContentMinHeight()
    }
    let activeSlide = $state(0)
    function nextSlide() {
        if (activeSlide < slides.length - 1) activeSlide += 1
        else activeSlide = 0
    }

    function prevSlide() {
        if (activeSlide > 0) activeSlide -= 1
        else activeSlide = slides.length - 1
    }
    let contentSliderEl: HTMLElement | null = $state(null)
    let imageContainerEl: HTMLElement | null = $state(null)
    onMount(() => {
        animateOnVisible(contentSliderEl, {
            translateY: ["50px", "0rem"],
            translateX: ["-50px", "0rem"],
            opacity: [0, 1],
            duration: 500,
            easing: "easeInOutQuad",
        })

        animateOnVisible(imageContainerEl, {
            translateY: ["50px", "0rem"],
            translateX: ["50px", "0rem"],
            opacity: [0, 1],
            duration: 500,
            easing: "easeInOutQuad",
        })

        void calculateContentMinHeight()
        window.addEventListener("resize", handleResize)
    })

    $effect(() => {
        const currentLang = String($lang || "")
        if (!currentLang) return
        void loadSlides(currentLang)
    })

    if (typeof window === "undefined") {
        const currentLang = String($lang || "")
        if (currentLang) {
            void loadSlides(currentLang)
        }
    }

    $effect(() => {
        if (!slides.length) {
            activeSlide = 0
            return
        }
        if (activeSlide >= slides.length) {
            activeSlide = 0
        }
    })

    onDestroy(() => {
        if (typeof window !== "undefined") {
            window.removeEventListener("resize", handleResize)
        }
    })
</script>

<section class="shiftedImageSlider">
    <div class="outer-wrapper">
        <ul class="content-slider" bind:this={contentSliderEl}>
            {#if loading}
                <li>
                    <div class="content" style={`min-height: ${contentMinHeight};`}>
                        <h3>{$_("general.loading")}</h3>
                    </div>
                </li>
            {:else if slides.length === 0}
                <li>
                    <div class="content" style={`min-height: ${contentMinHeight};`}>
                        <h3>{$_("general.no-content")}</h3>
                    </div>
                </li>
            {/if}
            {#each slides as slide, idx}
                <li class:active={idx === activeSlide}>
                    <div class="content" style={`min-height: ${contentMinHeight};`}>
                        <h3>{slide.pretitle}</h3>
                        <h2>
                            {#if slide.title[0].part1}{slide.title[0].part1}{/if}{#if slide.title[0].emphasized}<span
                                    class="color-orange">{slide.title[0].emphasized}</span
                                >{/if}{#if slide.title[0].part2}{slide.title[0].part2}
                            {/if}
                        </h2>
                        <p>{slide.description}</p>
                        <MoreButton text={$_("general.more-know")} path={slide.link} type="dark" size="big" />
                    </div>
                    <div class="arrows">
                        <button class="scroll-button left" onclick={() => prevSlide()}>
                            <img src="/media/leftChev.svg" alt="arrow-left" />
                        </button>
                        <button class="scroll-button right" onclick={() => nextSlide()}>
                            <img src="/media/rightChev.svg" alt="arrow-right" />
                        </button>
                    </div>
                </li>
            {/each}
        </ul>
        <div class="image-container-wrapper">
            <ul class="image-container" bind:this={imageContainerEl}>
                {#each slides as slide, idx}
                    <li class:active={idx === activeSlide}>
                        <MedialibImage id={slide.background_image[0]} maxWidth={500} />
                    </li>
                {/each}
            </ul>
        </div>
    </div>
</section>

<style lang="less" global>
    @import "../../../css/variables.less";
    .shiftedImageSlider {
        display: flex;
        position: relative;
        justify-content: center;
        margin: 4rem 0px;
        width: 100%;
        padding: 0px var(--default-padding);
        overflow: hidden;
        .outer-wrapper {
            display: flex;
            flex-direction: column;
            position: relative;
            max-width: var(--normal-max-width);
            width: 100%;

            .image-container-wrapper {
                position: absolute;
                right: 0px;
                min-height: 390px;
                height: 100%;
                max-width: 400px;
                width: 100%;
                .image-container {
                    position: relative;
                    width: 100%;
                    height: 100%;
                    li {
                        opacity: 0;
                        position: absolute;
                        top: 0px;
                        left: 0px;
                        width: 100%;
                        height: 100%;
                        img {
                            height: 100%;
                            width: 100%;
                            object-fit: cover;
                            transition: transform 0.3s;
                        }
                        &.active {
                            opacity: 1;
                        }
                    }
                }
            }
            &:hover {
                .image-container img {
                    transform: translate(-25px, -25px);
                }
                @media @max-mobile {
                    .image-container img {
                        transform: initial;
                    }
                }
            }
            @media @max-desktop {
                flex-direction: column-reverse;
                .image-container-wrapper {
                    position: relative;
                    max-width: calc(100vw - 2 * var(--default-padding));
                    max-height: calc(100vw - 2 * var(--default-padding));
                    min-height: calc(100vw - 2 * var(--default-padding));
                }
            }
            .content-slider {
                position: relative;
                width: 100%;
                z-index: 20;

                li {
                    position: absolute;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    align-items: space-between;
                    gap: 0.6rem;
                    transition: opacity 0.3s;
                    z-index: 1;
                    opacity: 0;
                    @media @max-desktop {
                        height: auto;
                    }
                    &.active {
                        opacity: 1;
                        z-index: 2;
                        position: relative;
                    }

                    .content {
                        display: flex;
                        flex-direction: column;
                        gap: 1.2rem;
                        padding-top: 2.4rem;
                        @media @max-desktop {
                            padding-top: 1.2rem;
                            min-height: 380px !important;
                        }
                        h2 {
                            display: flex;
                            flex-wrap: wrap;
                            gap: 0.5rem;
                            @media @max-desktop {
                                gap: 0.1rem;
                            }
                            span,
                            & {
                                font-size: 4.8rem;
                                font-weight: 400;
                                line-height: 5rem;
                                word-wrap: break-word;
                                color: var(--dark-100);

                                &.color-orange {
                                    color: var(--brand-100);
                                }
                                @media @max-desktop {
                                    font-size: 2.2rem;
                                    line-height: 2.6rem;
                                }
                            }
                        }
                        p {
                            max-width: 50%;
                            @media @max-desktop {
                                max-width: 100%;
                            }
                        }
                    }
                    .arrows {
                        display: flex;
                        gap: 1.2rem;
                    }
                }
            }
        }
    }
</style>
